import posthtml from 'posthtml/lib';
const ID_PREFIX = 'id-';
const DEFAULT_BREAKPOINT = 480;
export const DESKTOP_ONLY_CLASS = 'em-desktop-only';
export const MOBILE_ONLY_CLASS = 'em-mobile-only';

// Mark where a mobile only element starts and ends in the rendered HTML, so
// that the Outlook-only blocks MJML renders inside it can be removed.
const MOBILE_ONLY_START = '<!--em-mobile-only-->';
const MOBILE_ONLY_END = '<!--/em-mobile-only-->';
const MOBILE_ONLY_BLOCK = new RegExp(`${MOBILE_ONLY_START}([\\s\\S]*?)${MOBILE_ONLY_END}`, 'g');
// A downlevel-hidden conditional comment (`<!--[if mso | IE]>…<![endif]-->`),
// shown only by Outlook and old IE. Not `<!--[if !mso]>`, which other clients
// show.
const OUTLOOK_ONLY_BLOCK = /<!--\[if (?!!)[^\]]*\]>[\s\S]*?<!\[endif\]-->/g;
const getElementId = node => {
  var _node$attrs;
  return String(((_node$attrs = node.attrs) == null ? void 0 : _node$attrs['css-class']) ?? '').split(/\s+/).find(name => name.startsWith(ID_PREFIX));
};
const isElement = node => typeof node === 'object' && node !== null && 'tag' in node;
const withClass = (node, className) => {
  var _node$attrs2;
  return {
    ...node,
    attrs: {
      ...node.attrs,
      'css-class': [(_node$attrs2 = node.attrs) == null ? void 0 : _node$attrs2['css-class'], className].filter(Boolean).join(' ')
    }
  };
};
const element = function (tag, content) {
  if (content === void 0) {
    content = [];
  }
  return {
    tag,
    attrs: {},
    content
  };
};
const raw = content => element('mj-raw', [content]);
const findElement = (nodes, tag) => {
  for (const node of nodes ?? []) {
    if (isElement(node)) {
      if (node.tag === tag) {
        return node;
      }
      const found = findElement(node.content, tag);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};

/** The width at and above which MJML lays the email out for desktop. */
const getBreakpoint = nodes => {
  var _findElement;
  return parseInt(String((_findElement = findElement(nodes, 'mj-breakpoint')) == null || (_findElement = _findElement.attrs) == null ? void 0 : _findElement.width), 10) || DEFAULT_BREAKPOINT;
};
const buildStyle = breakpoint => element('mj-style', [`@media only screen and (max-width:${breakpoint - 1}px) {` + ` .${DESKTOP_ONLY_CLASS} { display:none !important; max-height:0 !important; overflow:hidden !important; } }` + ` @media only screen and (min-width:${breakpoint}px) {` + ` .${MOBILE_ONLY_CLASS} { display:none !important; max-height:0 !important; overflow:hidden !important; } }`]);

/**
 * Applies each element's visibility from the dynamic content: a hidden element
 * is removed; a desktop or mobile only one is hidden on the other device by a
 * media query on the MJML breakpoint. Outlook on desktop ignores media
 * queries, so a mobile only element is also kept out of it by a conditional
 * comment. Needs the composer's element ids, so it runs before they are
 * stripped.
 */
export const applyMjmlVisibility = _ref => {
  let mjml = _ref.mjml,
    _ref$dynamicContent = _ref.dynamicContent,
    dynamicContent = _ref$dynamicContent === void 0 ? {} : _ref$dynamicContent;
  if (!Object.values(dynamicContent).some(entry => entry.visibility)) {
    return mjml;
  }
  let isDeviceSpecific = false;
  const visibilityOf = node => {
    var _dynamicContent$id;
    const id = getElementId(node);
    return id ? (_dynamicContent$id = dynamicContent[id]) == null ? void 0 : _dynamicContent$id.visibility : undefined;
  };
  const applyToNodes = (nodes, inMobileOnly) => nodes.flatMap(node => {
    if (!isElement(node)) {
      return [node];
    }
    const visibility = visibilityOf(node);
    const applied = Array.isArray(node.content) ? {
      ...node,
      content: applyToNodes(node.content.flat(), inMobileOnly || visibility === 'mobile')
    } : node;
    switch (visibility) {
      case 'hidden':
        return [];
      case 'desktop':
        isDeviceSpecific = true;
        return [withClass(applied, DESKTOP_ONLY_CLASS)];
      case 'mobile':
        isDeviceSpecific = true;

        // Outlook does not nest conditional comments, so one inside a
        // mobile only element is left to the one around it.
        return inMobileOnly ? [withClass(applied, MOBILE_ONLY_CLASS)] : [raw(`<!--[if !mso]><!-->${MOBILE_ONLY_START}`), withClass(applied, MOBILE_ONLY_CLASS), raw(`${MOBILE_ONLY_END}<!--<![endif]-->`)];
      default:
        return [applied];
    }
  });
  const plugin = tree => {
    // The tree of a whole document is the list of its top-level nodes.
    const nodes = tree;
    nodes.splice(0, nodes.length, ...applyToNodes([...nodes], false));
    if (!isDeviceSpecific) {
      return tree;
    }
    const root = findElement(nodes, 'mjml');
    if (!root) {
      return tree;
    }
    const content = root.content ?? [];
    const head = content.find(node => isElement(node) && node.tag === 'mj-head') ?? element('mj-head');
    if (!content.includes(head)) {
      content.unshift(head);
    }
    head.content = [...(head.content ?? []), buildStyle(getBreakpoint(nodes))];
    root.content = content;
    return tree;
  };
  return posthtml([plugin]).process(mjml, {
    sync: true,
    xmlMode: true
  }).html;
};

/**
 * Removes the Outlook-only blocks MJML renders inside a mobile only element
 * (a section's Outlook tables, a divider's or social bar's Outlook markup).
 * The element is already wrapped in `<!--[if !mso]>`, so they would never
 * show, and Outlook does not nest conditional comments: the first inner
 * `<![endif]-->` would end the wrapper and show the rest on desktop.
 */
export const removeOutlookBlocksFromMobileOnly = html => html.includes(MOBILE_ONLY_START) ? html.replace(MOBILE_ONLY_BLOCK, (_, inner) => inner.replace(OUTLOOK_ONLY_BLOCK, '')) : html;
//# sourceMappingURL=apply-mjml-visibility.js.map